/**
 * Pure helpers for EntityUpsertForm possessive-group layout.
 * No React or app dependencies so they can be unit-tested in isolation.
 */

/** Minimal attribute shape for splitting (name/id/value used for normalization). */
export interface AttrShape {
  name?: string;
  id?: string;
  value?: string;
  [key: string]: unknown;
}

function normalizeAttrName(attr: AttrShape): string {
  const raw = attr.name || attr.id || attr.value || '';
  return (raw && typeof raw === 'string' ? raw : '').trim().toLowerCase().replace(/\s+/g, '');
}

/**
 * Split attributes into possessive-group vs other (for layout='possessive-group').
 * Group names are normalized (trim, lowercase, collapse spaces) for matching.
 */
export function splitSchemaAttributesByPossessiveGroup<T extends AttrShape>(
  schemaAttributes: T[],
  possessiveGroupNames: string[]
): { possessiveAttrs: T[]; otherAttrs: T[] } {
  const possessiveSet = new Set(
    possessiveGroupNames.map((n) => (n && typeof n === 'string' ? n : '').trim().toLowerCase().replace(/\s+/g, ''))
  );
  const possessiveAttrs: T[] = [];
  const otherAttrs: T[] = [];
  schemaAttributes.forEach((attr) => {
    const normalized = normalizeAttrName(attr);
    if (possessiveSet.has(normalized)) {
      possessiveAttrs.push(attr);
    } else {
      otherAttrs.push(attr);
    }
  });
  return { possessiveAttrs, otherAttrs };
}
